import { parseLocalDate } from "../common/service/CommonService";
import { AE_DIAGNOSIS_OPTIONS } from "../api/modules/common.option";

const getOnlyDate = (dateVal: any): Date | null => {
  if (!dateVal) return null;
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const validateFieldByName = (
  fieldKey: string,
  value: string | number,
  max: number,
  label: string,
  isRequired: boolean = false,
  minvalue?: number,
  maxvalue?: number,
) => {
  // const safeValue = value ?? "";
  const safeValue = String(value ?? "");
  const trimmed = safeValue.trim();

  if (minvalue !== undefined && (value as number) < minvalue) {
    return `${label} must be at least ${minvalue}`;
  }

  if (maxvalue !== undefined && (value as number) > maxvalue) {
    return `${label} must be at most ${maxvalue}`;
  }

  // Required / Optional handling
  if (!trimmed) {
    if (isRequired) {
      return `${label} is required`;
    }
    return true;
  }

  let pattern: RegExp;
  let message: string;

  switch (fieldKey) {
    case "receiptnumber":
    case "courierno":
      if (/\s{2,}/.test(trimmed)) {
        return "Multiple consecutive spaces are not allowed";
      }
      pattern = /^[A-Za-z0-9\s_-]+$/;
      message = "Only letters, numbers, spaces, hyphens and underscores are allowed";
      break;

    case "question":
    case "boxname":
      if (/\s{2,}/.test(trimmed)) {
        return "Multiple consecutive spaces are not allowed";
      }
      pattern = /^[A-Za-z0-9\s.,!?()\-/:;"&@#%+'_]+$/;
      message =
        "Only letters, numbers, and basic symbols (. , ! ? ( ) - _ / : ; \" & @ # % + ') are allowed";
      break;

    case "studytitle":
    case "ethicscommitteename":
      pattern = /^[A-Za-z0-9\s\-()'&]+$/;
      message = "Only letters, numbers, spaces, and (- ( ) ' &) are allowed";
      break;

    case "studycode":
      pattern = /^[A-Za-z0-9\/]+(-[A-Za-z0-9\/]+)*$/;
      message =
        "Only letters, numbers, forward slashes, and single hyphens are allowed";
      break;






    case "versionnumber":
      pattern = /^[A-Za-z0-9]+([.-][A-Za-z0-9]+)*$/;
      message = "Only letters, numbers, and (- .) are allowed";
      break;

    case "regulatoryauthority":
      pattern = /^[A-Za-z0-9\s,().'&-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - &) are allowed";
      break;
    case "aliquot_number":
      pattern = /^[A-Za-z0-9]{3}$/;
      message = "Only 3 alphanumeric characters are allowed (e.g., AS1, CS1)";
      break;
    // Sponsor & CRO Name, Adverse Event Term
    case "sponsorName":
    case "croName":
    case "eventterm":
    case "unitname":
    case "roomname":
    case "bedname":
      if (/\s{2,}/.test(trimmed)) {
        return "Multiple consecutive spaces are not allowed";
      }
      pattern = /^[A-Za-z0-9\s,().'&-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - &) are allowed";
      break;

    case "username":
    case "firstname":
    case "lastname":
    case "password":
    case "role":
    case "labelclaim":
      if (trimmed.length > max) {
        return `${label} cannot exceed ${max} characters`;
      }
      return true;

    // Adverse Event Other Action Taken & Event Narrative
    case "otheraction":
    case "eventnarrative":
    case "reportname":
    case "reasonforunfit":
    case "symptomsremark":
    case "examremark":
    case "investigationadvised":
    case "diagnosisremarks":
    case "brandname":
    case "genericname":
    case "frequency":
    case "interaction":
    case "finaldiagnosis":
      if (trimmed.length > max) {
        return `${label} cannot exceed ${max} characters`;
      }
      return true;

    //subject enrollment
    case "replacereason":
    case "replacementreason":
      if (trimmed.length > max) {
        return `${label} cannot exceed ${max} characters`;
      }
      return true;

    // Code List
    case "codelistname":
    case "codelistvalue":
      if (/\s{2,}/.test(trimmed)) {
        return "Multiple consecutive spaces are not allowed";
      }
      pattern = /^[A-Za-z0-9\s.,!?()\-/:;"&@#%+_]+$/;
      message =
        'Only letters, numbers,and basic symbols (. , ! ? ( ) - _ / : ; " & @ # % +) are allowed';
      break;

    // Template
    case "templatename":
      if (/\s{2,}/.test(trimmed)) {
        return "Multiple consecutive spaces are not allowed";
      }
      pattern = /^[A-Za-z0-9\s,().'&-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - &) are allowed";
      break;

    // Workflow
    case "workflowname":
    case "stagename":
    case "actionlabel":
    case "stagestatus":
      if (/\s{2,}/.test(trimmed)) {
        return "Multiple consecutive spaces are not allowed";
      }
      pattern = /^[A-Za-z0-9\s,().'&-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - &) are allowed";
      break;

    case "sponsorAddress":
      pattern = /^[A-Za-z0-9\s,./()'&:-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . / ( ) ' & : -) are allowed";
      break;

    case "sponsorContactPerson":
    case "croContactPerson":
      pattern = /^\d{10}$/;
      message = "Mobile number must be exactly 10 digits";
      break;

    case "aliquot_sort":
    case "period_sort":
    case "period_number":
    case "numberoftreatments":
    case "totalsamplingpoints":
    case "predosecount":
    case "postdosecount":
    case "ambulatorytotalsamplingpoints":
    case "samplingpoint":
    case "samplingpoints":
    case "screeningvalidity":
    case "watervolume":
    case "washoutdays":
    case "agemax":
    case "agemin":
    case "totalsubjects":
    case "numberofsequences":
    case "numberofperiods":
    case "predosewindowminutes":
      pattern = /^[0-9]*$/;
      message = "Only numbers are allowed";
      break;

    case "bmimax":
    case "bmimin":
      pattern = /^[0-9].*$/;
      message = "Only numbers and . are allowed";
      break;

    case "versionno":
      pattern = /^(?!0(\.0+)?$)\d+(\.\d)?$/;
      message = "Only positive numbers with one decimal place are allowed";
      break;

    case "testdrugname":
      pattern = /^[A-Za-z0-9\s,().'/-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - /) are allowed";
      break;
    case "dosageform":
    case "testdosageform":
      pattern = /^[A-Za-z0-9\s,().'/-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - /) are allowed";
      break;
    case "teststrength":
      pattern = /^[A-Za-z0-9\s/.%()-]+$/;
      message = "Only letters, numbers, spaces, and (. / % ( ) -) are allowed";
      break;
    case "testbatchno":
      pattern = /^[A-Za-z0-9_-]+$/;
      message = "Only letters, numbers, and (- _) are allowed";
      break;
    case "manufacturername":
    case "testmanufacturer":
      pattern = /^[A-Za-z0-9\s&.,()'-]+$/;
      message =
        "Only letters, numbers, spaces, and (& , . ( ) ' -) are allowed";
      break;

    case "refdrugname":
      pattern = /^[A-Za-z0-9\s,().'/-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - /) are allowed";
      break;
    case "refdosageform":
    case "reasonforclearance":
      pattern = /^[A-Za-z0-9\s,().'/-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - /) are allowed";
      break;
    case "strength":
    case "refstrength":
      pattern = /^[A-Za-z0-9\s/.%()-]+$/;
      message = "Only letters, numbers, spaces, and (. / % ( ) -) are allowed";
      break;
    case "refbatchno":
      pattern = /^[A-Za-z0-9_-]+$/;
      message = "Only letters, numbers, and (- _) are allowed";
      break;
    case "refmanufacturer":
      pattern = /^[A-Za-z0-9\s&.,()'-]+$/;
      message =
        "Only letters, numbers, spaces, and (& , . ( ) ' -) are allowed";
      break;

    case "dosestrength":
      pattern = /^[A-Za-z0-9\s/.%()-]+$/;
      message = "Only letters, numbers, spaces, and (/ . % ( ) -) are allowed";
      break;
    case "foodrestrictions":
      pattern = /^[A-Za-z0-9\s,./:()&'-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . / : ( ) & ' -) are allowed";
      break;
    case "subjectinterval":
      pattern = /^[A-Za-z0-9\s().-]+$/;
      message = "Only letters, numbers, spaces, and ( . ( ) - ) are allowed";
      break;

    case "sampleprocessingmethod":
      pattern = /^[A-Za-z0-9\s,().'/-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - /) are allowed";
      break;
    case "storagetemperature":
      pattern = /^[A-Za-z0-9\s°-]+$/;
      message = "Only letters, numbers, spaces, and (° -) are allowed";
      break;

    // For Study Geography
    case "countriescount":
      pattern = /^[0-9]+$/;
      message = "Only numeric values are allowed";
      break;

    case "plannedsitescount":
      pattern = /^[0-9]+$/;
      message = "Only numeric values are allowed";
      break;

    case "targetenrolment":
      pattern = /^[1-9][0-9]*$/;
      message = "Target Enrolment must be greater than 0";
      break;

    // For Study Role
    case "title":
    case "training":
      pattern = /^[A-Za-z0-9\s-]+$/;
      message = "Only letters, numbers, spaces, and - are allowed";
      break;
    case "traininggroup":
      pattern = /^[A-Za-z0-9\s&-]+$/;
      message = "Only letters, numbers, spaces, & and - are allowed";
      break;
    case "description":
      pattern = /^[A-Za-z0-9\s.,()\-\/:]+$/;
      message = "Only letters, numbers, spaces, and . , ( ) - / : are allowed";
      break;

    // For Code List
    case "codelistcode":
    case "unitcode":
    case "roomcode":
    case "bednumber":
      if (/\s{2,}/.test(trimmed)) {
        return "Multiple consecutive spaces are not allowed";
      }
      pattern = /^[A-Za-z0-9_-]+$/;
      message = "Only letters, numbers, _ and - are allowed";
      break;

    case "verificationpin":
      pattern = /^[0-9]{4}$/;
      message = "PIN must be exactly 4 digits";
      break;

    case "fullname":
      if (/\s{2,}/.test(trimmed)) {
        return "Multiple consecutive spaces are not allowed";
      }
      pattern = /^[A-Za-z ]+$/;
      message = "Only alphabets and spaces are allowed";
      break;

    case "initials":
      pattern = /^[A-Za-z]+$/;
      message = "Only alphabets allowed";
      break;

    case "mobilenum":
      pattern = /^\d{10}$/;
      message = "Mobile number must be exactly 10 digits";
      break;

    case "email":
      pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      message = "Enter a valid email address";
      break;

    case "address":
      pattern = /^[A-Za-z0-9\s,./()'&:-]+$/;
      message = "Only letters, numbers, spaces (,./()'&:-) are allowed";
      break;

    case "state":
      pattern = /^[A-Za-z\s]+$/;
      message = "Only alphabets and spaces are allowed";
      break;

    case "indicatorlightcolor":
      pattern = /^[A-Za-z\s]+$/;
      message = "Only alphabets and spaces are allowed";
      break;

    case "city":
      pattern = /^[A-Za-z\s.\-]+$/;
      message = "Only alphabets and spaces are allowed";
      break;
    case "deliveredby":
    case "allergies":
      pattern = /^[A-Za-z0-9\s,.()\-\/]+$/;
      message = "Only letters, numbers, spaces, . , : ( ) - / are allowed";
      break;

    case "pincode":
      pattern = /^\d{6}$/;
      message = "PIN Code must be exactly 6 digits";
      break;

    case "emergencyname":
      pattern = /^[A-Za-z ]+$/;
      message = "Only alphabets and spaces are allowed";
      break;

    case "emergencynumber":
      pattern = /^\d{10}$/;
      message = "Emergency Contact Phone must be exactly 10 digits";
      break;

    case "height": {
      const trimmedVal = String(value ?? "").trim();
      if (!/^\d+(\.\d+)?$/.test(trimmedVal)) {
        return "Only numeric values are allowed";
      }
      const h = Number(trimmedVal);
      if (h < 50 || h > 250) {
        return "Height must be between 50 and 250 cm";
      }
      return true;
    }

    case "weight": {
      const trimmedVal = String(value ?? "").trim();
      if (!/^\d+(\.\d+)?$/.test(trimmedVal)) {
        return "Only numeric values are allowed";
      }
      const w = Number(trimmedVal);
      if (w < 10 || w > 300) {
        return "Weight must be between 10 and 300 kg";
      }
      return true;
    }

    case "pulserate": {
      const trimmedVal = String(value ?? "").trim();
      if (!/^\d+$/.test(trimmedVal)) {
        return "Only integers are allowed";
      }
      const p = Number(trimmedVal);
      if (p < 30 || p > 250) {
        return "Pulse Rate must be between 30 and 250 bpm";
      }
      return true;
    }

    case "temperature": {
      const trimmedVal = String(value ?? "").trim();
      if (!/^\d+(\.\d+)?$/.test(trimmedVal)) {
        return "Only numeric values are allowed";
      }
      const t = Number(trimmedVal);
      if (t < 30 || t > 45) {
        return "Temperature must be between 30 and 45 °C";
      }
      return true;
    }

    case "bac": {
      const trimmedVal = String(value ?? "").trim();
      if (!trimmedVal) return true;
      if (!/^\d+(\.\d+)?$/.test(trimmedVal)) {
        return "Only numeric values are allowed";
      }
      const num = Number(trimmedVal);
      if (num < 0 || num > 1000) {
        return "Alcohol value must be between 0 and 1000";
      }
      if (trimmedVal.length > 6) {
        return "Maximum 6 characters allowed";
      }
      return true;
    }

    case "medicalhistory":
      pattern = /^[A-Za-z0-9\s,.:()\-\/]+$/;
      message = "Only letters, numbers, spaces, . , : ( ) - / are allowed";
      break;

    case "currentmedication":
      pattern = /^[A-Za-z0-9\s,.()\-\/]+$/;
      message = "Only letters, numbers, spaces, . , : ( ) - / are allowed";
      break;

    case "studyid":
    case "studyCode":
      pattern = /^[A-Za-z0-9_\-\/]+$/;
      message = "Only letters, numbers, _, - and / are allowed";
      break;
    case "subjectno":
      pattern = /^[A-Za-z0-9_-]+$/;
      message = "Only letters, numbers, _ and - are allowed";
      break;
    case "totalcontainers":
    case "receivedqty":
      pattern = /^[0-9]+$/;
      message = "Only numeric values are allowed";
      break;

    case "retentionqty":
    case "availableQty":
    case "qtyPerSubject":
    case "totalQty":
    case "qtyReturned":
    case "expectedUnusedQty":
    case "discrepancy":
    case "trainingqty":
    case "physicalverificationqty":
      pattern = /^[0-9]+$/;
      message = "Only numeric values are allowed";
      break;

    case "assaydifference":
      pattern = /^-?\d+(\.\d{1,2})?$/;
      message =
        "Only numbers (positive or negative) and up to 2 decimal places are allowed";
      break;

    case "productname":
      pattern = /^[A-Za-z0-9\s,().'/-]+$/;
      message =
        "Only letters, numbers, spaces, and (, . ( ) ' - /) are allowed";
      break;

    case "genericname":
      pattern = /^[A-Za-z0-9\s()/-]+$/;
      message = "Only letters, numbers, spaces, and (- ( ) /) are allowed";
      break;

    case "regulatorysubmission":
      pattern = /^[A-Za-z0-9\s,.:;()'"&/%#@+\-\/]+$/;
      message =
        "Only letters, numbers, spaces, and (, . : ; ( ) ' \" & / % # @ + -) are allowed";
      break;

    case "allottedpharmacyid":
    case "quarantineinstrumentid":
    case "dataloggerid":
      pattern = /^[A-Za-z0-9\s_-]+$/;
      message = "Only letters, numbers, spaces, and (- _) are allowed";
      break;

    case "storagecondition":
      pattern = /^[A-Za-z0-9\s,.:;()'"°&%<>=+\-/]+$/;
      message =
        "Only letters, numbers, spaces, and (, . : ; ( ) ' \" ° & % < > = + - /) are allowed";
      break;

    case "batchno":
    case "lotno":
    case "batchlotno":
    case "batchLotNumber":
      pattern = /^[A-Za-z0-9_-]+$/;
      message = "Only letters, numbers, _ and - are allowed";
      break;

    case "plasmavolume":
      pattern = /^[A-Za-z0-9\s.]+$/;
      message = "Only letters, numbers, spaces, and dots (.) are allowed";
      break;

    case "remarks":
    case "comments":
    case "physicalconditionremarks":
    case "detailedremarks":
    case "ipdescription":
    case "qtyforstudyremarks":
    case "qtyforretentionremarks":
    case "dataloggerremarks":
    case "packingconditionremarks":
      pattern = /^[A-Za-z0-9\s,().'-]+$/;
      message = "Only letters, numbers, spaces, and (, . ( ) ' -) are allowed";
      break;

    default:
      return true; // no validation required
  }

  if (!pattern.test(trimmed)) {
    return message;
  }

  // Length check
  if (trimmed.length > max) {
    return `${label} cannot exceed ${max} characters`;
  }

  return true;
};

export const validations = {
  // 📦 Dispatch Box Form
  dispatchBox: {
    boxname: {
      validate: (value: string) =>
        validateFieldByName("boxname", value, 50, "Box Name", true),
    },
    storagetemperature: {
      validate: (value: string) =>
        validateFieldByName(
          "storagetemperature",
          value,
          50,
          "Storage Temperature",
          true,
        ),
    },
    remarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 250, "Remarks", false),
    },
  },


  // 📦 Dispatch Box Form
  dispatchReceipt: {
    receiptnumber: {
      validate: (value: string) =>
        validateFieldByName("receiptnumber", value, 50, "Receipt Number", true),
    },
    courierno: {
      validate: (value: string) =>
        validateFieldByName("courierno", value, 50, "Courier Number", true),
    },
    receiptdate: {
      validate: (value: string) =>
        validateFieldByName("receiptdate", value, 50, "Receipt Date", true),
    },
  },

  // 🧊 Storage Management Form Rules
  storageUnit: {
    storagecode: {
      validate: (value: string) =>
        validateFieldByName("studycode", value, 30, "Storage Code", true),
    },
    storagename: {
      validate: (value: string) =>
        validateFieldByName("question", value, 50, "Storage Name", true),
    },
    targettemperature: {
      validate: (value: string) =>
        validateFieldByName("storagetemperature", value, 20, "Target Temperature", true),
    },
    room: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 50, "Room / Area", true),
    },
  },

  storageItem: {
    barcode: {
      validate: (value: string) =>
        validateFieldByName("studycode", value, 50, "Barcode", true),
    },
    reason: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 100, "Movement Reason", true),
    },
  },

  // 🔐 Login Form
  login: {
    email: {
      validate: (value: string) =>
        validateFieldByName("email", value, 100, "Email", true),
    },
    password: {
      validate: (value: string) =>
        validateFieldByName("password", value, 15, "Password", true),
    },
    username: {
      validate: (value: string) =>
        validateFieldByName("username", value, 50, "Username", true),
    },
    firstname: {
      validate: (value: string) =>
        validateFieldByName("firstname", value, 50, "First Name", true),
    },
    lastname: {
      validate: (value: string) =>
        validateFieldByName("lastname", value, 50, "Last Name", true),
    },
  },

  transfer: {
    remarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 250, "Remarks", false),
    },
  },

  userAssignment: {
    username: {
      validate: (value: string) =>
        validateFieldByName("username", value, 30, "UserName", true),
    },
    role: {
      validate: (value: string) =>
        validateFieldByName("role", value, 30, "Role", true),
    },
  },

  studyLite: {
    extrasubjects: {
      validate: (value: string, formValues?: any) => {
        const baseResult = validateFieldByName("extrasubjects", value, 3, "Stand-By Subjects", false,1,500);
        if (baseResult !== true) return baseResult;

        if (value !== "" && value !== null && value !== undefined && formValues?.totalsubjects) {
          const extra = Number(value);
          const total = Number(formValues.totalsubjects);
          if (!isNaN(extra) && !isNaN(total) && extra > total) {
            return "No. of Stand-By Subjects cannot be greater than Planned Subjects";
          }
        }
        return true;
      },
    },
    totalsubjects: {
      validate: (value: string) =>
        validateFieldByName("totalsubjects", value, 3, "Planned Subjects", false, 1, 500),
    }
  },

  studyDetails: {
    studytitle: {
      validate: (value: string) =>
        validateFieldByName("studytitle", value, 255, "Study Title", false),
    },
    studycode: {
      validate: (value: string) =>
        validateFieldByName("studycode", value, 100, "Study Code", true),
    },
    versionnumber: {
      validate: (value: string) =>
        validateFieldByName(
          "versionnumber",
          value,
          20,
          "Version Number",
          false,
        ),
    },

    sponsorname: {
      validate: (value: string) =>
        validateFieldByName("sponsorname", value, 150, "Sponsor Name", true),
    },
    ethicscommitteename: {
      validate: (value: string) =>
        validateFieldByName(
          "ethicscommitteename",
          value,
          255,
          "Ethics Committee Name",
          false,
        ),
    },
    regulatoryauthority: {
      validate: (value: string) =>
        validateFieldByName(
          "regulatoryauthority",
          value,
          255,
          "Regulatory Authority",
          false,
        ),
    },

    testdrugname: {
      validate: (value: string) =>
        validateFieldByName(
          "testdrugname",
          value,
          255,
          "Test Drug Name",
          false,
        ),
    },
    testdosageform: {
      validate: (value: string) =>
        validateFieldByName("testdosageform", value, 100, "Dosage Form", false),
    },
    teststrength: {
      validate: (value: string) =>
        validateFieldByName("teststrength", value, 50, "Strength", false),
    },
    testbatchno: {
      validate: (value: string) =>
        validateFieldByName("testbatchno", value, 50, "Batch No", false),
    },
    testmanufacturer: {
      validate: (value: string) =>
        validateFieldByName(
          "testmanufacturer",
          value,
          150,
          "Manufacturer",
          false,
        ),
    },
    refdrugname: {
      validate: (value: string) =>
        validateFieldByName(
          "refdrugname",
          value,
          255,
          "Reference Drug Name",
          false,
        ),
    },
    refdosageform: {
      validate: (value: string) =>
        validateFieldByName(
          "refdosageform",
          value,
          100,
          "Reference Dosage Form",
          false,
        ),
    },
    refstrength: {
      validate: (value: string) =>
        validateFieldByName("refstrength", value, 50, "Strength", false),
    },
    refbatchno: {
      validate: (value: string) =>
        validateFieldByName("refbatchno", value, 50, "Batch No", false),
    },
    refmanufacturer: {
      validate: (value: string) =>
        validateFieldByName(
          "refmanufacturer",
          value,
          150,
          "Manufacturer",
          false,
        ),
    },

    dosestrength: {
      validate: (value: string) =>
        validateFieldByName("dosestrength", value, 50, "Dose Strength", false),
    },
    foodrestrictions: {
      validate: (value: string) =>
        validateFieldByName(
          "foodrestrictions",
          value,
          255,
          "Food Restrictions",
          false,
        ),
    },
    subjectinterval: {
      validate: (value: string) =>
        validateFieldByName(
          "subjectinterval",
          value,
          10,
          "Subject Interval",
          true,
          0,
          1440,
        ),
    },
    predosewindowminutes: {
      validate: (value: string | number) =>
        validateFieldByName(
          "predosewindowminutes",
          value as string,
          4,
          "Predose Window Minutes",
          false,
          0,
          1440,
        ),
    },

    sampleprocessingmethod: {
      validate: (value: string) =>
        validateFieldByName(
          "sampleprocessingmethod",
          value,
          255,
          "Sample Processing Method",
          false,
        ),
    },
    storagetemperature: {
      validate: (value: string) =>
        validateFieldByName(
          "storagetemperature",
          value,
          50,
          "Storage Temperature",
          false,
        ),
    },

    numberoftreatments: {
      validate: (value: string) =>
        validateFieldByName(
          "numberoftreatments",
          value,
          2,
          "Number of Treatments",
          false,
          0,
          10,
        ),
    },
    numberofperiods: {
      validate: (value: string) =>
        validateFieldByName(
          "numberofperiods",
          value,
          2,
          "Number of Periods",
          false,
          0,
          20,
        ),
    },
    numberofsequences: {
      validate: (value: string) =>
        validateFieldByName(
          "numberofsequences",
          value,
          2,
          "Number of Sequences",
          false,
          0,
          10,
        ),
    },
    totalsubjects: {
      validate: (value: string) =>
        validateFieldByName(
          "totalsubjects",
          value,
          3,
          "Total Subjects",
          false,
          1,
          500,
        ),
    },
    agemin: {
      validate: (value: string) =>
        validateFieldByName(
          "agemin",
          value,
          3,
          "Age Range (Min)",
          false,
          0,
          120,
        ),
    },
    agemax: {
      validate: (value: string) =>
        validateFieldByName(
          "agemax",
          value,
          3,
          "Age Range (Max)",
          false,
          0,
          120,
        ),
    },
    bmimin: {
      validate: (value: string) =>
        validateFieldByName(
          "bmimin",
          value,
          8,
          "BMI Range (Min)",
          false,
          0,
          60,
        ),
    },
    bmimax: {
      validate: (value: string) =>
        validateFieldByName(
          "bmimax",
          value,
          8,
          "BMI Range (Max)",
          false,
          0,
          60,
        ),
    },
    washoutdays: {
      validate: (value: string) =>
        validateFieldByName(
          "washoutdays",
          value,
          3,
          "Washout Period (days)",
          true,
          0,
          60,
        ),
    },
    screeningvalidity: {
      validate: (value: string) =>
        validateFieldByName(
          "screeningvalidity",
          value,
          3,
          "Screening validity",
          true,
          0,
          365,
        ),
    },
    watervolume: {
      validate: (value: string) =>
        validateFieldByName(
          "watervolume",
          value,
          4,
          "Water Volume (ml)",
          false,
          0,
          1000,
        ),
    },
    totalsamplingpoints: {
      validate: (value: string) =>
        validateFieldByName(
          "totalsamplingpoints",
          value,
          2,
          "Total Sampling Points",
          false,
          1,
          30,
        ),
    },
    predosecount: {
      validate: (value: string) =>
        validateFieldByName(
          "predosecount",
          value,
          2,
          "Pre Dose Sampling Count",
          false,
          0,
          30,
        ),
    },
    postdosecount: {
      validate: (value: string) =>
        validateFieldByName(
          "postdosecount",
          value,
          2,
          "Post Dose Sampling Count",
          false,
          0,
          30,
        ),
    },
  },

  studyScreening: {
    reasonforunfit: {
      validate: (value: string) =>
        validateFieldByName(
          "reasonforunfit",
          value,
          255,
          "Reason for Unfit",
          true,
        ),
    },
  },

  volunteerEnrollment: {
    replacereason: {
      validate: (value: string) =>
        validateFieldByName(
          "replacereason",
          value,
          255,
          "Replacement Reason",
          true,
        ),
    },
    replacementreason: {
      validate: (value: string) =>
        validateFieldByName(
          "replacementreason",
          value,
          255,
          "Replacement Reason",
          true,
        ),
    },
  },

  ipInventory: {
    // ── Section 1: Study & Product Info ──
    studyid: {
      validate: (value: string) =>
        validateFieldByName("studyid", value, 50, "Study Code", true),
    },
    regulatorysubmission: {
      validate: (value: string) =>
        validateFieldByName(
          "regulatorysubmission",
          value,
          150,
          "Regulatory of Study Submission",
          true,
        ),
    },
    ipcategory: {
      validate: (value: string) =>
        validateFieldByName("ipcategory", value, 100, "IP Category", true),
    },
    productname: {
      validate: (value: string) =>
        validateFieldByName("productname", value, 255, "Product Name", true),
    },
    genericname: {
      validate: (value: string) =>
        validateFieldByName("genericname", value, 150, "Generic Name", true),
    },
    strength: {
      validate: (value: string) =>
        validateFieldByName("strength", value, 100, "Strength ", false),
    },
    dosageform: {
      validate: (value: string) =>
        validateFieldByName("dosageform", value, 100, "Dosage Form ", false),
    },
    labelclaim: {
      validate: (value: string) =>
        validateFieldByName("labelclaim", value, 100, "Label Claim", true),
    },

    // ── Section 2: Batch & Receipt Details ──
    batchlotno: {
      validate: (value: string) =>
        validateFieldByName("batchlotno", value, 50, "Batch / Lot No.", true),
    },
    manufacturername: {
      validate: (value: string) =>
        validateFieldByName(
          "manufacturername",
          value,
          150,
          "Manufacturer Name",
          true,
        ),
    },
    manufacturingdate: {
      validate: (value: string, formValues: any) => {
        if (!value) return "Manufacturing Date is required";
        const mfg = getOnlyDate(value);
        if (!mfg) return "Please enter a valid Manufacturing Date";

        const today = new Date();
        const todayOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        if (mfg > todayOnly) {
          return "Manufacturing Date cannot be in the future";
        }

        if (formValues?.expirydate) {
          const exp = getOnlyDate(formValues.expirydate);
          if (exp && mfg >= exp) {
            return "Manufacturing Date must be before Expiry / Retest Date";
          }
        }
        if (formValues?.dateofreceipt) {
          const receipt = getOnlyDate(formValues.dateofreceipt);
          if (receipt && mfg > receipt) {
            return "Manufacturing Date must be before Date of Receipt";
          }
        }
        return true;
      },
      deps: ["expirydate", "dateofreceipt"],
    },
    expirydate: {
      validate: (value: string, formValues: any) => {
        if (!value) return "Expiry / Retest Date is required";
        const exp = getOnlyDate(value);
        if (!exp) return "Please enter a valid Expiry / Retest Date";

        const today = new Date();
        const todayOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        if (exp <= todayOnly) {
          return "Expiry / Retest Date must be in the future";
        }

        if (formValues?.manufacturingdate) {
          const mfg = getOnlyDate(formValues.manufacturingdate);
          if (mfg && exp <= mfg) {
            return "Expiry / Retest Date must be after Manufacturing Date";
          }
        }
        return true;
      },
      deps: ["manufacturingdate"],
    },
    receivedqty: {
      validate: (value: number) =>
        validateFieldByName(
          "receivedqty",
          value,
          6,
          "Received Quantity",
          true,
          0,
        ),
    },
    unit: {
      validate: (value: string) =>
        validateFieldByName("unit", value, 50, "Unit", false),
    },
    containertype: {
      validate: (value: string) =>
        validateFieldByName(
          "containertype",
          value,
          50,
          "Container Type",
          false,
        ),
    },
    totalcontainers: {
      validate: (value: number) =>
        validateFieldByName(
          "totalcontainers",
          value,
          10,
          "Total Containers",
          false,
          1,
          100,
        ),
    },
    dateofreceipt: {
      validate: (value: string, formValues: any) => {
        if (!value) return "Date of Receipt is required";
        const receipt = getOnlyDate(value);
        if (!receipt) return "Please enter a valid Date of Receipt";

        const today = new Date();
        const todayOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        if (receipt > todayOnly) {
          return "Date of Receipt cannot be in the future";
        }

        if (formValues?.manufacturingdate) {
          const mfg = getOnlyDate(formValues.manufacturingdate);
          if (mfg && receipt <= mfg) {
            return "Date of Receipt must be after Manufacturing Date";
          }
        }
        return true;
      },
      deps: ["manufacturingdate"],
    },
    deliveredby: {
      validate: (value: string) =>
        validateFieldByName("deliveredby", value, 150, "Delivered By", false),
    },

    // ── Section 3: Storage & Condition ──
    storagecondition: {
      validate: (value: string) =>
        validateFieldByName(
          "storagecondition",
          value,
          255,
          "Required Storage Condition",
          true,
        ),
    },
    dataloggerid: {
      validate: (value: string) =>
        validateFieldByName("dataloggerid", value, 50, "Data Logger ID", false),
    },
    dataloggerremarks: {
      validate: (value: string) =>
        validateFieldByName(
          "dataloggerremarks",
          value,
          255,
          "Data Logger Remarks",
          true,
        ),
    },
    physicalcondition: {
      validate: (value: string) =>
        validateFieldByName(
          "physicalcondition",
          value,
          100,
          "Physical Condition on Receipt",
          true,
        ),
    },
    comments: {
      validate: (value: string) =>
        validateFieldByName(
          "comments",
          value,
          250,
          "Additional Comments / Discrepancies",
          false,
        ),
    },
    physicalconditionremarks: {
      validate: (value: string) =>
        validateFieldByName(
          "physicalconditionremarks",
          value,
          255,
          "Physical Condition Remark",
          false,
        ),
    },
    qtyforstudyremarks: {
      validate: (value: string) =>
        validateFieldByName(
          "qtyforstudyremarks",
          value,
          255,
          "Remarks for Study Quantity",
          true,
        ),
    },
    qtyforretentionremarks: {
      validate: (value: string) =>
        validateFieldByName(
          "qtyforretentionremarks",
          value,
          255,
          "Remarks for Retention Quantity",
          true,
        ),
    },
    packingconditionremarks: {
      validate: (value: string) =>
        validateFieldByName(
          "packingconditionremarks",
          value,
          255,
          "Packing Condition Remarks",
          true,
        ),
    },

    // ── Section 4: Storage & Condition ──
    allottedpharmacyid: {
      validate: (value: string) =>
        validateFieldByName(
          "allottedpharmacyid",
          value,
          50,
          "Allotted Serially Pharmacy ID No.",
          true,
        ),
    },
    quarantineinstrumentid: {
      validate: (value: string) =>
        validateFieldByName(
          "quarantineinstrumentid",
          value,
          50,
          "Quarantine Instrument ID No.",
          true,
        ),
    },
  },

  ipacceptance: {
    trainingqty: {
      validate: (value: string) =>
        validateFieldByName(
          "trainingqty",
          value,
          10,
          "Training Quantity",
          false,
        ),
    },
    physicalverificationqty: {
      validate: (value: string) =>
        validateFieldByName(
          "physicalverificationqty",
          value,
          10,
          "Physical Verification Quantity",
          false,
        ),
    },
    assaydifference: {
      validate: (value: string) =>
        validateFieldByName(
          "assaydifference",
          value,
          6,
          "Assay Difference",
          false,
        ),
    },
    ipdescription: {
      validate: (value: string) =>
        validateFieldByName(
          "ipdescription",
          value,
          500,
          "Description of IP",
          false,
        ),
    },
    comments: {
      validate: (value: string) =>
        validateFieldByName(
          "comments",
          value,
          255,
          "Additional Comments / Discrepancies",
          false,
        ),
    },
  },

  lineclearance: {
    reasonforclearance: {
      validate: (value: string) => validateFieldByName("reasonforclearance", value, 255, "Reason for Clearance", false),
    },
  },

  dispensingLog: {
    // ── Section 1: Study Information ──
    study: {
      validate: (value: string) =>
        validateFieldByName("study", value, 50, "Study Code", true),
    },
    investigator: {
      validate: (value: string) =>
        validateFieldByName(
          "investigator",
          value,
          150,
          "Principal Investigator",
          false,
        ),
    },
    studyTypeText: {
      validate: (value: string) =>
        validateFieldByName("studyTypeText", value, 100, "Study Type", false),
    },

    // ── Section 2: Investigational Product Details ──
    productname: {
      validate: (value: string) =>
        validateFieldByName("productname", value, 100, "Product Name", false),
    },
    ipcategory: {
      validate: (value: string) =>
        validateFieldByName("ipcategory", value, 100, "IP Category", false),
    },
    batchlotno: {
      validate: (value: string) =>
        validateFieldByName(
          "batchlotno",
          value,
          255,
          "Batch/Lot Number",
          false,
        ),
    },
    availableQty: {
      validate: (value: string) =>
        validateFieldByName(
          "availableQty",
          value,
          6,
          "Available Quantity",
          false,
        ),
    },
    expiry: {
      validate: (value: string) =>
        validateFieldByName("expiry", value, 20, "Expiry Date", false),
    },

    // ── Section 3: Subject Allocation & Dosing ──
    startSubject: {
      validate: (value: string) =>
        validateFieldByName(
          "startSubject",
          value,
          4,
          "Start Subject No.",
          true,
        ),
    },
    endSubject: {
      validate: (value: string) =>
        validateFieldByName("endSubject", value, 4, "End Subject No.", true),
    },
    qtyPerSubject: {
      validate: (value: string) =>
        validateFieldByName(
          "qtyPerSubject",
          value,
          4,
          "Quantity per Subject",
          true,
        ),
    },
    totalQty: {
      validate: (value: string) =>
        validateFieldByName(
          "totalQty",
          value,
          6,
          "Total Quantity Needed",
          false,
        ),
    },

    // ── Section 4: Dispensing Information ──
    dispensedBy: {
      validate: (value: string) =>
        validateFieldByName("dispensedBy", value, 150, "Dispensed By", true),
    },
    verifiedBy: {
      validate: (value: string) =>
        validateFieldByName("verifiedBy", value, 150, "Verified By", true),
    },
    date: {
      validate: (value: string) =>
        validateFieldByName("date", value, 20, "Dispensing Date", true),
    },
    time: {
      validate: (value: string) =>
        validateFieldByName("time", value, 10, "Time", true),
    },
    notes: {
      validate: (value: string) =>
        validateFieldByName("notes", value, 500, "Notes/Deviation", false),
    },
  },

  returnsLog: {
    // ── Section 1: General Information ──
    studyCode: {
      validate: (value: string) =>
        validateFieldByName("studyCode", value, 50, "Study Code", true),
    },
    returnDate: {
      validate: (value: string) =>
        validateFieldByName("returnDate", value, 20, "Return Date", true),
    },
    returnSourceType: {
      validate: (value: string) =>
        validateFieldByName(
          "returnSourceType",
          value,
          50,
          "Return Source Type",
          true,
        ),
    },
    subjectId: {
      validate: (value: string) =>
        validateFieldByName("subjectId", value, 100, "Subject Number", true),
    },

    // ── Section 2: Product Information ──
    dispensingId: {
      validate: (value: string) =>
        validateFieldByName("dispensingId", value, 50, "Dispensing ID", false),
    },
    productname: {
      validate: (value: string) =>
        validateFieldByName("productname", value, 255, "Product Name", true),
    },
    batchLotNumber: {
      validate: (value: string) =>
        validateFieldByName(
          "batchLotNumber",
          value,
          100,
          "Batch / Lot Number",
          true,
        ),
    },
    ipcategory: {
      validate: (value: string) =>
        validateFieldByName("ipcategory", value, 100, "IP Category", true),
    },
    unitType: {
      validate: (value: string) =>
        validateFieldByName("unitType", value, 50, "Unit Type", true),
    },

    // ── Section 3: Return Details ──
    qtyReturned: {
      validate: (value: string) =>
        validateFieldByName("qtyReturned", value, 6, "Quantity Returned", true),
    },
    expectedUnusedQty: {
      validate: (value: string) =>
        validateFieldByName(
          "expectedUnusedQty",
          value,
          6,
          "Expected Unused Qty",
          true,
        ),
    },
    discrepancy: {
      validate: (value: string) =>
        validateFieldByName("discrepancy", value, 6, "Discrepancy", false),
    },
    condition: {
      validate: (value: string) =>
        validateFieldByName(
          "condition",
          value,
          50,
          "Condition of Returned IP",
          true,
        ),
    },
    reason: {
      validate: (value: string) =>
        validateFieldByName("reason", value, 50, "Reason for Return", true),
    },
    comments: {
      validate: (value: string) =>
        validateFieldByName("comments", value, 500, "Comments / Notes", false),
    },
  },

  studyGeography: {
    countriescount: {
      validate: (value: string) =>
        validateFieldByName(
          "countriescount",
          value,
          3,
          "Countries Count",
          true,
        ),
    },
    plannedsitescount: {
      validate: (value: string) =>
        validateFieldByName(
          "plannedsitescount",
          value,
          4,
          "Planned Site",
          true,
        ),
    },
    targetenrolment: {
      validate: (value: string) =>
        validateFieldByName(
          "targetenrolment",
          value,
          6,
          "Target Enrolment",
          true,
        ),
    },
  },

  studyRole: {
    title: {
      validate: (value: string) =>
        validateFieldByName("title", value, 40, "Role Title", true),
    },
    description: {
      validate: (value: string) =>
        validateFieldByName("description", value, 255, "Description", true),
    },
  },
  trainingGroup: {
    traininggroup: {
      validate: (value: string) =>
        validateFieldByName(
          "traininggroup",
          value,
          100,
          "Training Group",
          true,
        ),
    },
    description: {
      validate: (value: string) =>
        validateFieldByName("description", value, 255, "Description", true),
    },
  },

  aliquot: {
    aliquot_sort: {
      validate: (value: string) =>
        validateFieldByName(
          "aliquot_sort",
          value,
          2,
          "Aliquot Number",
          true,
          1,
          20,
        ),
    },

    aliquot_number: {
      validate: (value: string) =>
        validateFieldByName(
          "aliquot_number",
          value,
          5,
          "Display Aliquot",
          true,
        ),
    },
  },

  period: {
    period_sort: {
      validate: (value: string) =>
        validateFieldByName(
          "period_sort",
          value,
          2,
          "Period Number",
          true,
          1,
          20,
        ),
    },
    period: {
      period_sort: {
        validate: (value: string) =>
          validateFieldByName(
            "period_sort",
            value,
            2,
            "Period Number",
            true,
            1,
            20,
          ),
      },

      period_number: {
        validate: (value: string) =>
          validateFieldByName(
            "period_number",
            value,
            2,
            "Display Sequence",
            true,
            1,
            20,
          ),
      },
    },
    period_number: {
      validate: (value: string) =>
        validateFieldByName(
          "period_number",
          value,
          2,
          "Display Sequence",
          true,
          1,
          20,
        ),
    },
  },

  sponsor: {
    sponsorName: {
      validate: (value: string) =>
        validateFieldByName("sponsorName", value, 150, "Sponsor Name", true),
    },

    croName: {
      validate: (value: string) =>
        validateFieldByName("croName", value, 150, "CRO Name", true),
    },

    sponsorAddress: {
      validate: (value: string) =>
        validateFieldByName(
          "sponsorAddress",
          value,
          500,
          "Sponsor Address",
          false,
        ),
    },

    sponsorContactPerson: {
      validate: (value: string) =>
        validateFieldByName(
          "sponsorContactPerson",
          value,
          10,
          "Sponsor Contact",
          false,
        ),
    },

    contactEmail: {
      validate: (value: string) =>
        validateFieldByName("email", value, 150, "Contact Email", true),
    },

    croEmail: {
      validate: (value: string) =>
        validateFieldByName("email", value, 150, "CRO Email", false),
    },

    croContactPerson: {
      validate: (value: string) =>
        validateFieldByName(
          "croContactPerson",
          value,
          10,
          "CRO Contact",
          false,
        ),
    },
  },

  // 👤 User Form
  user: {
    username: {
      validate: (value: string) =>
        validateFieldByName("username", value, 50, "Username", true),
    },
    firstname: {
      validate: (value: string) =>
        validateFieldByName("firstname", value, 50, "First Name", true),
    },
    lastname: {
      validate: (value: string) =>
        validateFieldByName("lastname", value, 50, "Last Name", true),
    },
    email: {
      validate: (value: string) =>
        validateFieldByName("email", value, 150, "Email", true),
    },
    password: {
      validate: (value: string) =>
        validateFieldByName("password", value, 15, "Password", true),
    },
    name: {
      validate: (value: string) =>
        validateFieldByName("fullname", value, 100, "Name", true),
    },
    phone: {
      validate: (value: string) =>
        validateFieldByName("mobilenum", value, 10, "Phone", true),
    },
    role: {
      validate: (value: string) =>
        validateFieldByName("role", value, 100, "Role", true),
    },
  },

  // ICF Form
  checklist: {
    question: {
      validate: (value: string) =>
        validateFieldByName("question", value, 255, "Question", true),
    },

    versionno: {
      validate: (value: string) =>
        validateFieldByName("versionno", value, 4, "Version No", true),
    },

    isRequired: {
      validate: (value: string) =>
        validateFieldByName("isrequired", value, 5, "Is Required", true),
    },

    answerLabels: {
      validate: (value: string) =>
        validateFieldByName(
          "checklisttype",
          value,
          100,
          "Checklist Type",
          true,
        ),
    },
  },

  // Design Form
  design: {
    designtitle: {
      validate: (value: string) =>
        validateFieldByName("designtitle", value, 50, "Design Title", true),
    },
    sequencetype: {
      validate: (value: string) =>
        validateFieldByName("sequencetype", value, 50, "Sequence Type", true),
    },
    period: {
      validate: (value: string) =>
        validateFieldByName("period", value, 2, "Period", true),
    },
    blocksize: {
      validate: (value: string) =>
        validateFieldByName("blocksize", value, 3, "Block Size", true),
    },
  },

  // 🗓 Master Period Form
  MasterPeriod: {
    period_sort: {
      validate: (value: string) =>
        validateFieldByName("period_sort", value, 2, "Period No", true, 1, 20),
    },
    period_number: {
      validate: (value: string) =>
        validateFieldByName(
          "period_number",
          value,
          2,
          "Display Sequence",
          true,
          1,
          20,
        ),
    },
  },
  adverseEvent: {
    studycode: {
      required: "Study Code is required",
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("studycode", value, 50, "Study Code", true);
      },
    },
    subjectno: {
      required: "Subject No. is required",
    },
    eventterm: {
      required: "Event Term / Description is required",
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName(
          "eventterm",
          value,
          255,
          "Event Term / Description",
          true,
        );
      },
    },
    onsetdate: {
      required: "Onset Date is required",
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("onsetdate", value, 20, "Onset Date", true);
      },
    },
    onsettime: (getValues: any) => ({
      required: "Onset Time is required",
      validate: (value: string) => {
        if (!value) return true;
        if (getValues) {
          const onsetDateVal = getValues("onsetdate");
          if (onsetDateVal) {
            const onsetDate = parseLocalDate(onsetDateVal);
            const today = new Date();
            if (
              onsetDate &&
              onsetDate.toDateString() === today.toDateString()
            ) {
              const [onsetH, onsetM] = value.split(":").map(Number);
              const currentH = today.getHours();
              const currentM = today.getMinutes();
              const onsetMinutes = onsetH * 60 + onsetM;
              const currentMinutes = currentH * 60 + currentM;

              if (onsetMinutes > currentMinutes) {
                return "Onset time cannot be in the future";
              }
            }
          }
        }
        return true;
      },
    }),
    lastdosedate: (getValues: any) => ({
      required:
        getValues && getValues("onsetofevent") === "postdose"
          ? "Last Dose Date is required"
          : false,
      validate: (value: string) => {
        if (!value) return true;
        if (getValues) {
          const onsetDateVal = getValues("onsetdate");
          if (onsetDateVal) {
            const onsetDate = parseLocalDate(onsetDateVal);
            const lastDoseDate = parseLocalDate(value);
            if (onsetDate && lastDoseDate) {
              onsetDate.setHours(0, 0, 0, 0);
              lastDoseDate.setHours(0, 0, 0, 0);

              if (lastDoseDate > onsetDate) {
                return "Last Dose Date cannot be after Onset Date";
              }
            }
          }
        }
        return true;
      },
    }),
    lastdosetime: (getValues: any) => ({
      required:
        getValues && getValues("onsetofevent") === "postdose"
          ? "Last Dose Time is required"
          : false,
      validate: (value: string) => {
        if (!value) return true;
        if (getValues) {
          const onsetDateVal = getValues("onsetdate");
          const onsetTimeVal = getValues("onsettime");
          const lastDoseDateVal = getValues("lastdosedate");

          if (onsetDateVal && lastDoseDateVal && onsetTimeVal) {
            const onsetDate = parseLocalDate(onsetDateVal);
            const lastDoseDate = parseLocalDate(lastDoseDateVal);
            if (onsetDate && lastDoseDate) {
              onsetDate.setHours(0, 0, 0, 0);
              lastDoseDate.setHours(0, 0, 0, 0);

              if (lastDoseDate.getTime() === onsetDate.getTime()) {
                const [onsetH, onsetM] = onsetTimeVal.split(":").map(Number);
                const [doseH, doseM] = value.split(":").map(Number);
                const onsetMinutes = onsetH * 60 + onsetM;
                const doseMinutes = doseH * 60 + doseM;

                if (doseMinutes > onsetMinutes) {
                  return "Last Dose Time cannot be after Onset Time";
                }
              }
            }
          }
        }
        return true;
      },
    }),
    medicalexamend: (getValues: any, isRequired?: boolean) => ({
      required: isRequired ? "Medical Exam End Time is required" : false,
      validate: (value: string) => {
        if (!value) return true;
        if (getValues) {
          const startVal = getValues("medicalexamstart");
          if (startVal) {
            const [startH, startM] = startVal.split(":").map(Number);
            const [endH, endM] = value.split(":").map(Number);
            const startMinutes = startH * 60 + startM;
            const endMinutes = endH * 60 + endM;

            if (endMinutes < startMinutes) {
              return "Medical Exam End Time cannot be before Start Time";
            }
          }
        }
        return true;
      },
    }),
    reportingdate: (getValues: any) => ({
      validate: (value: string) => {
        if (!value) return true;
        const onsetDateVal = getValues ? getValues("onsetdate") : null;

        if (onsetDateVal) {
          const onsetDate = parseLocalDate(onsetDateVal);
          const reportingDate = parseLocalDate(value);
          if (onsetDate && reportingDate) {
            onsetDate.setHours(0, 0, 0, 0);
            reportingDate.setHours(0, 0, 0, 0);

            if (reportingDate < onsetDate) {
              return "Reporting Date must be after or equal to Onset Date";
            }
          }
        }
        return true;
      },
    }),
    reportingtime: (getValues: any) => ({
      validate: (value: string) => {
        if (!value) return true;
        const onsetDateVal = getValues ? getValues("onsetdate") : null;
        const onsetTimeVal = getValues ? getValues("onsettime") : null;
        const reportingDateVal = getValues ? getValues("reportingdate") : null;

        if (onsetDateVal && reportingDateVal) {
          const onsetDate = parseLocalDate(onsetDateVal);
          const reportingDate = parseLocalDate(reportingDateVal);
          if (onsetDate && reportingDate) {
            onsetDate.setHours(0, 0, 0, 0);
            reportingDate.setHours(0, 0, 0, 0);

            if (reportingDate.getTime() === onsetDate.getTime()) {
              const [onsetH, onsetM] = (onsetTimeVal || "00:00").split(":");
              const [repH, repM] = (value || "00:00").split(":");
              const onsetMinutes =
                Number(onsetH || 0) * 60 + Number(onsetM || 0);
              const repMinutes = Number(repH || 0) * 60 + Number(repM || 0);
              if (repMinutes < onsetMinutes) {
                return "Reporting Time must be after or equal to Onset Time";
              }
            }
          }
        }
        return true;
      },
    }),
    eventstatus: {
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("eventstatus", value, 50, "Status", true);
      },
    },
    severitygrade: {
      required: "Severity is required",
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName(
          "severitygrade",
          value,
          50,
          "Severity Grade",
          true,
        );
      },
    },
    causality: {
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("causality", value, 50, "Causality", true);
      },
    },
    diagnosisremarks: (getValues: any) => ({
      required: getValues && (() => {
        const diagnosisVal = getValues("diagnosis");
        const selectedOption = AE_DIAGNOSIS_OPTIONS.find(opt => Number(opt.value) === Number(diagnosisVal));
        const label = selectedOption?.label?.toLowerCase() || "";
        return label === "other" || label.includes("final");
      })() ? "Diagnosis Remark is required" : false,
      validate: (value: string) => {
        if (getValues) {
          const diagnosisVal = getValues("diagnosis");
          const selectedOption = AE_DIAGNOSIS_OPTIONS.find(opt => Number(opt.value) === Number(diagnosisVal));
          const label = selectedOption?.label?.toLowerCase() || "";
          const isRequired = label === "other" || label.includes("final");

          if (isRequired && (!value || !value.trim())) {
            return "Diagnosis Remark is required";
          }
        }
        return validateFieldByName(
          "diagnosisremarks",
          value,
          255,
          "Diagnosis Remark",
          false,
        );
      },
    }),
    actiontaken: {
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName(
          "actiontaken",
          value,
          50,
          "Action Taken",
          true,
        );
      },
    },
    isadverse: {
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName(
          "isadverse",
          value,
          50,
          "Seriousness Assessment",
          true,
        );
      },
    },
    eventnarrative: {
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName(
          "eventnarrative",
          value,
          500,
          "Event Narrative",
          true,
        );
      },
    },
    otheraction: {
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName(
          "otheraction",
          value,
          255,
          "Other Action Taken",
          false,
        );
      },
    },
    symptomsremark: {
      required: "Symptoms Remark is required",
      validate: (value: string) =>
        validateFieldByName("symptomsremark", value, 500, "Remark", false),
    },
    examremark: {
      validate: (value: string) =>
        validateFieldByName("examremark", value, 500, "Remark", false),
    },
    investigationadvised: {
      validate: (value: string) =>
        validateFieldByName(
          "investigationadvised",
          value,
          500,
          "Investigation Advised",
          false,
        ),
    },
    bloodpressure: {
      validate: (value: string) => {
        if (!value) return true;
        const pattern = /^\d{2,3}\/\d{2,3}$/;
        if (!pattern.test(value)) {
          return "Blood Pressure must be in format 'systolic/diastolic' (e.g. 120/80)";
        }
        const [systolic, diastolic] = value.split("/").map(Number);
        if (systolic < 50 || systolic > 300) {
          return "Systolic pressure must be between 50 and 300 mm Hg";
        }
        if (diastolic < 30 || diastolic > 200) {
          return "Diastolic pressure must be between 30 and 200 mm Hg";
        }
        return true;
      },
    },
    pulse: {
      validate: (value: string | number) => {
        if (value === undefined || value === null || value === "") return true;
        const valStr = String(value);
        const pattern = /^\d+$/;
        if (!pattern.test(valStr)) {
          return "Pulse must be a positive integer";
        }
        const val = Number(valStr);
        if (val < 20 || val > 250) {
          return "Pulse rate must be between 30 and 250 beats/min";
        }
        return true;
      },
    },
    respiratoryrate: {
      validate: (value: string | number) => {
        if (value === undefined || value === null || value === "") return true;
        const valStr = String(value);
        const pattern = /^\d+$/;
        if (!pattern.test(valStr)) {
          return "Respiratory Rate must be a positive integer";
        }
        const val = Number(valStr);
        if (val < 5 || val > 60) {
          return "Respiratory Rate must be between 5 and 60 breaths/min";
        }
        return true;
      },
    },
    temperature: (getValues: any) => ({
      validate: (value: string | number) => {
        if (value === undefined || value === null || value === "") return true;
        const valStr = String(value);
        const pattern = /^\d{1,3}(\.\d{1})?$/;
        if (!pattern.test(valStr)) {
          return "Ensure that there are no more than 4 digits in total.";
        }
        const val = Number(valStr);

        const unit = getValues ? getValues("temperatureunit") : null;
        const location = getValues ? getValues("temperaturelocation") : null;

        if (unit === "C") {
          if (location === "Oral") {
            if (val < 35.0 || val > 42.0) {
              return "Oral temperature in Celsius must be between 35.0°C and 42.0°C";
            }
          } else if (location === "Axillary") {
            if (val < 34.0 || val > 41.0) {
              return "Axillary temperature in Celsius must be between 34.0°C and 41.0°C";
            }
          } else {
            if (val < 34.0 || val > 42.0) {
              return "Temperature in Celsius must be between 34.0°C and 42.0°C";
            }
          }
        } else if (unit === "F") {
          if (location === "Oral") {
            if (val < 95.0 || val > 107.6) {
              return "Oral temperature in Fahrenheit must be between 95.0°F and 107.6°F";
            }
          } else if (location === "Axillary") {
            if (val < 93.2 || val > 105.8) {
              return "Axillary temperature in Fahrenheit must be between 93.2°F and 105.8°F";
            }
          } else {
            if (val < 93.2 || val > 107.6) {
              return "Temperature in Fahrenheit must be between 93.2°F and 107.6°F";
            }
          }
        } else {
          if (val < 30 || val > 115) {
            return "Temperature must be between 30°C and 115°F";
          }
        }
        return true;
      },
    }),
    brandname: {
      required: "Brand Name is required",
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("brandname", value, 100, "Brand Name", true);
      },
    },
    genericname: {
      required: "Generic Name with Strength is required",
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("genericname", value, 150, "Generic Name with Strength", true);
      },
    },
    frequency: {
      required: "Frequency is required",
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("frequency", value, 50, "Frequency", true);
      },
    },
    interaction: {
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("interaction", value, 255, "Interaction with I.P.", false);
      },
    },
    finaldiagnosis: {
      required: "Final Diagnosis is required",
      validate: (value: string) => {
        if (!value) return true;
        return validateFieldByName("finaldiagnosis", value, 255, "Final Diagnosis", true);
      },
    },
  },

  drugRetention: {
    studyid: {
      validate: (value: string) =>
        validateFieldByName("studyid", value, 50, "Study Code", true),
    },
    productname: {
      validate: (value: string) =>
        validateFieldByName("productname", value, 255, "Product Name", true),
    },
    batchlotno: {
      validate: (value: string) =>
        validateFieldByName("batchlotno", value, 100, "Batch / Lot No.", true),
    },
    receivedqty: {
      validate: (value: string) =>
        validateFieldByName(
          "receivedqty",
          value,
          10,
          "Total Quantity Received",
          true,
        ),
    },
    retentionqty: {
      validate: (value: string) =>
        validateFieldByName(
          "retentionqty",
          value,
          10,
          "Quantity Allocated for Retention",
          true,
        ),
    },
    retentiontype: {
      validate: (value: string) =>
        validateFieldByName("retentiontype", value, 50, "Retention Type", true),
    },
    storagetype: {
      validate: (value: string) =>
        validateFieldByName("storagetype", value, 50, "Storage Type", true),
    },
    retentionstartdate: {
      validate: (value: string) =>
        validateFieldByName(
          "retentionstartdate",
          value,
          20,
          "Retention Start Date",
          true,
        ),
    },
    retentionperiod: {
      validate: (value: string) =>
        validateFieldByName(
          "retentionperiod",
          value,
          50,
          "Retention Period",
          true,
        ),
    },
  },

  checkin: {
    checkinDate: {
      validate: (value: string) =>
        validateFieldByName("checkinDate", value, 20, "Check-In Date", true),
    },
    checkinTime: {
      validate: (value: string) =>
        validateFieldByName("checkinTime", value, 10, "Check-In Time", true),
    },
    verificationpin: {
      validate: (value: string) =>
        validateFieldByName(
          "verificationpin",
          value,
          4,
          "Verification PIN",
          true,
        ),
    },
  },

  auditLog: {
    from: {
      validate: (value: string) =>
        validateFieldByName("from", value, 20, "From Date", true),
    },
    to: {
      validate: (value: string) =>
        validateFieldByName("to", value, 20, "To Date", true),
    },
    module: {
      validate: (value: string) =>
        validateFieldByName("module", value, 100, "Module", true),
    },
    submodule: {
      validate: (value: string) =>
        validateFieldByName("submodule", value, 100, "Sub Module", true),
    },
  },

  checkout: {
    checkoutDate: {
      validate: (value: string) =>
        validateFieldByName("checkoutDate", value, 20, "Check-Out Date", true),
    },
    checkoutTime: {
      validate: (value: string) =>
        validateFieldByName("checkoutTime", value, 10, "Check-Out Time", true),
    },
    remarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 500, "Remarks", false),
    },
  },

  vacutainerLabel: {
    studyid: {
      validate: (value: string) =>
        validateFieldByName("studyid", value, 50, "Study Code", true),
    },
    period: {
      validate: (value: string) =>
        validateFieldByName("period", value, 20, "Period", true),
    },
    timepoint: {
      validate: (value: string) =>
        validateFieldByName("timepoint", value, 50, "Timepoint", true),
    },
  },

  cryovialLabel: {
    studyid: {
      validate: (value: string) =>
        validateFieldByName("studyid", value, 50, "Study Code", true),
    },
    period: {
      validate: (value: string) =>
        validateFieldByName("period", value, 20, "Period", true),
    },
    timepoint: {
      validate: (value: string) =>
        validateFieldByName("timepoint", value, 50, "Timepoint", true),
    },
    aliquot: {
      validate: (value: string) =>
        validateFieldByName("aliquot", value, 20, "Aliquot Number", true),
    },
  },

  studyParticipant: {
    userId: {
      validate: (value: string) =>
        validateFieldByName("userId", value, 50, "User", true),
    },
    trainingGroupId: {
      validate: (value: string) =>
        validateFieldByName(
          "trainingGroupId",
          value,
          50,
          "Training Group",
          true,
        ),
    },
  },

  dispensingLabel: {
    studyid: {
      validate: (value: string) =>
        validateFieldByName("studyid", value, 10, "Study Code", true),
    },
    subjectno: {
      validate: (value: string) =>
        validateFieldByName("subjectno", value, 10, "Subject No", true),
    },
  },

  volunteer: {
    fullname: {
      validate: (value: string) =>
        validateFieldByName("fullname", value, 50, "Full name", true),
    },
    initials: {
      validate: (value: string) =>
        validateFieldByName("initials", value, 10, "Initials", true),
    },
    mobilenum: {
      validate: (value: any) =>
        validateFieldByName("mobilenum", value, 10, "Mobile", true),
    },
    email: {
      validate: (value: string) =>
        validateFieldByName("email", value, 150, "Email", true),
    },
    address: {
      validate: (value: string) =>
        validateFieldByName("address", value, 500, "Address", true),
    },
    state: {
      validate: (value: string) =>
        validateFieldByName("state", value, 100, "State", true),
    },
    city: {
      validate: (value: string) =>
        validateFieldByName("city", value, 100, "City", true),
    },
    pincode: {
      validate: (value: string) =>
        validateFieldByName("pincode", value, 6, "PIN Code", true),
    },
    emergencyname: {
      validate: (value: string) =>
        validateFieldByName(
          "emergencyname",
          value,
          100,
          "Emergency Contact Name",
          true,
        ),
    },
    emergencynumber: {
      validate: (value: any) =>
        validateFieldByName(
          "emergencynumber",
          value,
          10,
          "Emergency Contact Phone",
          true,
        ),
    },
    height: {
      validate: (value: string) =>
        validateFieldByName("height", value, 10, "Height (cm)", true),
    },
    weight: {
      validate: (value: string) =>
        validateFieldByName("weight", value, 10, "Weight (kg)", true),
    },
    bac: {
      validate: (value: any) =>
        validateFieldByName("bac", value, 6, "Alcohol value", true),
    },
    indicatorlightcolor: {
      validate: (value: any) =>
        validateFieldByName(
          "indicatorlightcolor",
          value,
          50,
          "Indicator Light Color",
          true,
        ),
    },
    allergies: {
      validate: (value: string) =>
        validateFieldByName("allergies", value, 500, "Allergies", true),
    },
    medicalhistory: {
      validate: (value: string) =>
        validateFieldByName(
          "medicalhistory",
          value,
          500,
          "Medical History",
          true,
        ),
    },
    currentmedication: {
      validate: (value: string) =>
        validateFieldByName(
          "currentmedication",
          value,
          500,
          "Current Medication",
          true,
        ),
    },
    pulserate: {
      validate: (value: string) =>
        validateFieldByName("pulserate", value, 3, "Pulse Rate", true),
    },
    temperature: {
      validate: (value: string) =>
        validateFieldByName("temperature", value, 5, "Temperature", true),
    },
  },
  workflow: {
    name: {
      validate: (value: string) =>
        validateFieldByName("workflowname", value, 250, "Workflow Name", true),
    },
    stagename: {
      validate: (value: string) =>
        validateFieldByName("stagename", value, 250, "Stage Name", true),
    },
    actionlabel: {
      validate: (value: string) =>
        validateFieldByName("actionlabel", value, 250, "Action Label", true),
    },
    status: {
      validate: (value: string) =>
        validateFieldByName("stagestatus", value, 250, "Status", true),
    },
  },
  codelist: {
    code: {
      validate: (value: string) =>
        validateFieldByName("codelistcode", value, 150, "Codelist Code", true),
    },
    name: {
      validate: (value: string) =>
        validateFieldByName("codelistname", value, 150, "Codelist Name", true),
    },
  },
  codelistValue: {
    name: {
      validate: (value: string) =>
        validateFieldByName("codelistname", value, 150, "Name", true),
    },
    value: {
      validate: (value: string) =>
        validateFieldByName("codelistvalue", value, 150, "Value", true),
    },
  },
  template: {
    name: {
      validate: (value: string) =>
        validateFieldByName("templatename", value, 250, "Template Name", true),
    },
  },
  labtest: {
    reportname: {
      validate: (value: string) =>
        validateFieldByName("reportname", value, 50, "Report Name", true),
    },
    remarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 100, "Remarks", false),
    },
  },
  studyicf: {
    remarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 500, "Remarks", true),
    },
  },
  blockvolunteer: {
    detailedremarks: {
      validate: (value: string) =>
        validateFieldByName(
          "detailedremarks",
          value,
          250,
          "Detailed Remarks",
          false,
        ),
    },
  },
  dropoutvolunteer: {
    detailedremarks: {
      validate: (value: string) =>
        validateFieldByName(
          "detailedremarks",
          value,
          250,
          "Detailed Remarks",
          false,
        ),
    },
  },
  transfervolunteer: {
    detailedremarks: {
      validate: (value: string) =>
        validateFieldByName(
          "detailedremarks",
          value,
          250,
          "Detailed Remarks",
          false,
        ),
    },
  },
  ambulatoryVisit: {
    visitstartday: {
      validate: (val: any) => {
        if (val === "" || val === undefined || val === null) return true;
        if (val === "-") return "Please enter a valid number after minus (-).";
        if (!/^-?\d+$/.test(String(val))) {
          return "Only numbers and minus (-) are allowed.";
        }
        if (Number(val) > 0) {
          return "Visit Window Start Day must be less than or equal to 0.";
        }
        if (Number(val) < -99) {
          return "Visit Window Start Day must be greater than or equal to -99.";
        }
        return true;
      },
    },
    visitendday: (getValues: any) => ({
      validate: (val: any) => {
        if (val === "" || val === undefined || val === null) return true;
        if (val === "+") return "Please enter a valid number after plus (+).";
        if (!/^\+?\d+$/.test(String(val))) {
          return "Only numbers and plus (+) are allowed.";
        }
        if (Number(val) < 0) {
          return "Visit Window End Day must be equal or greater than 0.";
        }
        if (Number(val) > 99) {
          return "Visit Window End Day must be less than or equal to 99.";
        }
        const from = getValues("visitstartday");
        if (
          from !== "" &&
          from !== "-" &&
          from !== "+" &&
          Number(val) < Number(from)
        ) {
          return "Must be greater than or equal to Visit Window Start Day.";
        }
        return true;
      },
    }),
    estimateddurationminutes: {
      validate: (val: any) => {
        if (val === "" || val === undefined || val === null) return true;
        if (Number(val) < 1) {
          return "Estimated Duration must be at least 1 minute";
        }
        if (Number(val) > 1440) {
          return "Estimated Duration cannot exceed 1 day (1440 minutes)";
        }
        return true;
      },
    },
    remarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 255, "Remarks", false),
    },
    detailedreason: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 255, "Detailed Reason", false),
    },
    detailedremarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 255, "Detailed Remarks", false),
    },
    visitname: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 255, "Visit Name", true),
    },
    totalsamplingpoints: {
      required: "Total Sampling Points is required",
    },
    samplingpoints: {
      required: "Sampling Points is required",
      validate: (val: any) => {
        if (val === "" || val === undefined || val === null) return true;
        if (!/^[0-9]+$/.test(String(val))) {
          return "Only numbers are allowed";
        }
        return true;
      },
    },
    deviation: {
      validate: (value: string) =>
        validateFieldByName("deviation", value, 255, "Deviation", false),
    },
    visitdescription: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 255, "Visit Description", false),
    },
    activityname: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 255, "Activity Name", true),
    },
    activitydescription: {
      validate: (value: string) =>
        validateFieldByName(
          "remarks",
          value,
          255,
          "Activity Description",
          false,
        ),
    },
    breathalcoholconcentration: {
      validate: (val: any) => {
        if (val === "" || val === undefined || val === null) return true;
        const strVal = String(val).trim();
        if (!strVal) return true;
        if (!/^\d+(\.\d+)?$/.test(strVal)) {
          return "Please enter a valid numeric value";
        }
        if (!/^\d{1,8}(\.\d{1,2})?$/.test(strVal)) {
          return "Max 8 digits before decimal and 2 decimal places allowed (e.g., 0.00)";
        }
        const num = Number(strVal);
        if (num < 0) {
          return "Alcohol concentration cannot be negative";
        }
        if (num > 99999999.99) {
          return "Alcohol concentration cannot exceed 99999999.99";
        }
        return true;
      },
    },
  },
  subjectPeriod: {
    reason: {
      required: "Reason for Correction is required",
      validate: (value: string) =>
        validateFieldByName(
          "detailedremarks",
          value,
          250,
          "Reason for Correction",
          true,
        ),
    },
    deviationReason: {
      required: "Deviation reason explanation is required",
      validate: (value: string) =>
        validateFieldByName(
          "detailedremarks",
          value,
          250,
          "Deviation Reason",
          true,
        ),
    },
    presetReason: {
      required: "Please select a standard reason",
    },
  },
  postStudyAssessment: {
    position: {
      required: "Position is required",
    },
    actualTime: {
      required: "Actual Time (Hrs.) is required",
    },
    bloodPressure: {
      required: "Blood Pressure (mmHg) is required",
      validate: (value: string) => {
        if (!value || !String(value).trim()) return "Blood Pressure (mmHg) is required";
        if (!/^\d{2,3}\/\d{2,3}$/.test(String(value).trim())) {
          return "Please enter a valid blood pressure format (e.g. 120/80)";
        }
        return true;
      },
    },
    pulseRate: {
      required: "Pulse Rate (bpm) is required",
      validate: (value: string) => {
        if (!value || !String(value).trim()) return "Pulse Rate (bpm) is required";
        if (!/^\d+$/.test(String(value).trim())) return "Only integers are allowed";
        const p = Number(String(value).trim());
        if (p < 30 || p > 250) return "Pulse Rate must be between 30 and 250 bpm";
        return true;
      },
    },
    respirationRate: {
      required: "Respiration Rate (/min) is required",
      validate: (value: string) => {
        if (!value || !String(value).trim()) return "Respiration Rate (/min) is required";
        if (!/^\d+$/.test(String(value).trim())) return "Only integers are allowed";
        const r = Number(String(value).trim());
        if (r < 4 || r > 60) return "Respiration Rate must be between 4 and 60 breaths/min";
        return true;
      },
    },
    oralTemp: {
      required: "Oral Temp (°F) is required",
      validate: (value: string) => {
        if (!value || !String(value).trim()) return "Oral Temp (°F) is required";
        if (!/^\d+(\.\d+)?$/.test(String(value).trim())) return "Only numeric values are allowed";
        const t = Number(String(value).trim());
        if (t < 90 || t > 108) return "Oral Temp must be between 90 and 108 °F";
        return true;
      },
    },
    vitalWellBeing: {
      required: "Well Being Assessment is required",
    },
    respiratorySystem: {
      required: "Respiratory System assessment is required",
    },
    gastroIntestinalSystem: {
      required: "Gastro Intestinal System assessment is required",
    },
    cardiovascularSystem: {
      required: "Cardiovascular System assessment is required",
    },
    centralNervousSystem: {
      required: "Central Nervous System assessment is required",
    },
    wellBeingAssessment: {
      required: "Well-being Assessment is required",
    },
    participantStatus: {
      required: "Participant's Status is required",
    },
    remarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 500, "Remarks", false),
    },
    // Laboratory Assessment Fields
    bloodSamplesCollected: {
      required: "Blood samples collected status is required",
    },
    bloodVolumeCollected: {
      validate: (value: any) => {
        if (!value || !String(value).trim()) return true;
        if (!/^\d+(\.\d+)?$/.test(String(value).trim())) {
          return "Volume must be a valid numeric value";
        }
        return true;
      },
    },
    clinicallySignificantAbnormal: {
      required: "Clinically significant abnormal value status is required",
    },
    abnormalDetails: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 500, "If Yes, provide details", false),
    },
  },

  bedManagement: {
    unit: {
      unitcode: {
        validate: (value: string) =>
          validateFieldByName("unitcode", value, 20, "Unit Code", true),
      },
      unitname: {
        validate: (value: string) =>
          validateFieldByName("unitname", value, 25, "Unit Name", true),
      },
      description: {
        validate: (value: string) =>
          validateFieldByName("description", value, 255, "Description", false),
      },
    },
    room: {
      roomname: {
        validate: (value: string) =>
          validateFieldByName("roomname", value, 25, "Room Name / Number", true),
      },
      roomcode: {
        validate: (value: string) =>
          validateFieldByName("roomcode", value, 20, "Room Code", true),
      },
      description: {
        validate: (value: string) =>
          validateFieldByName("description", value, 255, "Description", false),
      },
    },
    bed: {
      bednumber: {
        validate: (value: string) =>
          validateFieldByName("bednumber", value, 25, "Bed Number", true),
      },
      bedname: {
        validate: (value: string) =>
          validateFieldByName("bedname", value, 25, "Bed Name", true),
      },
    },
  },

  dosingSchedule: {
    plasmavolume: {
      required: "Plasma Volume is required",
      validate: (value: string) =>
        validateFieldByName("plasmavolume", value, 50, "Plasma Volume", true),
    },
    remarks: {
      validate: (value: string) =>
        validateFieldByName("remarks", value, 500, "Remarks", false),
    },
  },
};
